import { useSessionKey } from "@auth/sessionStore";
import { CompanionPanel } from "@components/CompanionPanel";
import type { Room } from "@rooms/roomsClient";
import { useRoomsList } from "@rooms/roomsQueries";
import {
  EmptyNote,
  LabelTape,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  PanelError,
  SkeletonStack,
  Tag,
} from "@scribenote/brian-design-system";
import { formatDateTime, formatRelativeTimeMs } from "@utils/format-date";

import { AddDisplayButton } from "./AddDisplayButton";
import { BoardViewsButton } from "./BoardViewsButton";
import { DeleteDisplayButton } from "./DeleteDisplayButton";
import { DISPLAY_KIND_LABEL, type ListedDisplay } from "./displaysClient";
import {
  isPairing,
  useDisplaysList,
  usePendingPairings,
} from "./displaysQueries";
import { PairDisplayButton } from "./PairDisplayButton";
import { UnpairDisplayButton } from "./UnpairDisplayButton";

const ScreenStatus = ({
  device,
  pairing,
}: Pick<ListedDisplay, "device"> & { pairing: boolean }) =>
  pairing ? (
    <span className="flex flex-col" aria-live="polite">
      <span className="text-sm text-ink">Pairing</span>
      <span className="font-mono text-xs text-annotation">
        waiting for the screen
      </span>
    </span>
  ) : device ? (
    <span
      className="flex flex-col"
      title={`Paired ${formatDateTime(device.pairedAt)}`}
    >
      <span className="text-sm text-ink">Paired</span>
      <span className="font-mono text-xs text-annotation">
        seen {formatRelativeTimeMs(Date.parse(device.lastSeenAt))}
      </span>
    </span>
  ) : (
    <span className="text-sm text-annotation">Not paired</span>
  );

const DisplayRow = ({
  display,
  room,
  pairing,
  sessionKey,
}: {
  display: ListedDisplay;
  room: Room | undefined;
  pairing: boolean;
  sessionKey: string;
}) => (
  <LedgerRow data-testid="admin-display-row">
    <LedgerCell>
      <span className="block max-w-[20rem] truncate font-medium text-ink">
        {display.name}
      </span>
    </LedgerCell>
    <LedgerCell>
      <Tag>{DISPLAY_KIND_LABEL[display.kind]}</Tag>
    </LedgerCell>
    <LedgerCell>
      {room ? (
        <LabelTape className="max-w-[12rem] truncate align-middle">
          {room.name}
        </LabelTape>
      ) : (
        <span className="text-sm text-annotation">No room</span>
      )}
    </LedgerCell>
    <LedgerCell>
      <ScreenStatus device={display.device} pairing={pairing} />
    </LedgerCell>
    <LedgerCell align="right" className="w-px whitespace-nowrap">
      <span className="inline-flex items-center gap-2">
        {display.rotation && (
          <BoardViewsButton
            display={display}
            saved={display.rotation}
            sessionKey={sessionKey}
          />
        )}
        {display.device ? (
          <UnpairDisplayButton display={display} sessionKey={sessionKey} />
        ) : (
          !pairing && (
            <PairDisplayButton display={display} sessionKey={sessionKey} />
          )
        )}
        <DeleteDisplayButton display={display} sessionKey={sessionKey} />
      </span>
    </LedgerCell>
  </LedgerRow>
);

const DisplaysList = ({ sessionKey }: { sessionKey: string }) => {
  const { data: displays } = useDisplaysList(sessionKey);
  const pendingPairings = usePendingPairings(sessionKey);
  const { data: rooms } = useRoomsList(sessionKey);
  const roomsById = new Map(rooms.map((room) => [room.id, room]));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <AddDisplayButton rooms={rooms} sessionKey={sessionKey} />
      </div>
      {displays.length === 0 ? (
        <EmptyNote>No screens set up yet.</EmptyNote>
      ) : (
        <LedgerTable data-testid="admin-display-list">
          <LedgerHead>
            <tr>
              <LedgerHeadCell>Name</LedgerHeadCell>
              <LedgerHeadCell>Kind</LedgerHeadCell>
              <LedgerHeadCell>Room</LedgerHeadCell>
              <LedgerHeadCell>Screen</LedgerHeadCell>
              <LedgerHeadCell align="right" className="w-px">
                Actions
              </LedgerHeadCell>
            </tr>
          </LedgerHead>
          <LedgerBody>
            {displays.map((display) => (
              <DisplayRow
                key={display.id}
                display={display}
                room={
                  display.roomId ? roomsById.get(display.roomId) : undefined
                }
                pairing={
                  !display.device && isPairing(pendingPairings, display.id)
                }
                sessionKey={sessionKey}
              />
            ))}
          </LedgerBody>
        </LedgerTable>
      )}
    </div>
  );
};

export const DisplaysPanel = () => {
  const sessionKey = useSessionKey();

  return (
    <CompanionPanel
      skeleton={<SkeletonStack count={3} itemClassName="h-10" />}
      fallback={(retry) => <PanelError label="displays" onRetry={retry} />}
    >
      <DisplaysList sessionKey={sessionKey} />
    </CompanionPanel>
  );
};
